const initializeMessageUnreadMonitor = () => {
    const stateUrl =
        document.body?.dataset
            .unreadMessageStateUrl;

    if (!stateUrl) {
        return;
    }

    if (
        document.body.dataset
            .unreadMessageMonitorInitialized
        === 'true'
    ) {
        return;
    }

    document.body.dataset
        .unreadMessageMonitorInitialized =
        'true';

    let latestUnreadMessageId = 0;
    let requestInFlight = false;
    let pollTimer = null;

    const updateBadges = (count) => {
        const normalizedCount =
            Math.max(
                0,
                Number.parseInt(
                    String(count ?? 0),
                    10
                ) || 0
            );

        document
            .querySelectorAll(
                '[data-unread-message-badge]'
            )
            .forEach((badge) => {
                badge.textContent =
                    normalizedCount > 99
                        ? '99+'
                        : String(
                            normalizedCount
                        );

                const label =
                    `${normalizedCount} unread ${
                        normalizedCount === 1
                            ? 'conversation'
                            : 'conversations'
                    }`;

                badge.title = label;

                badge.setAttribute(
                    'aria-label',
                    label
                );

                badge.classList.toggle(
                    'hidden',
                    normalizedCount < 1
                );
            });
    };

    const removeToast = (toast) => {
        if (!toast || !toast.isConnected) {
            return;
        }

        toast.classList.add(
            'translate-y-[-0.5rem]',
            'opacity-0'
        );

        window.setTimeout(
            () => toast.remove(),
            250
        );
    };

    const showNewMessageToast = (
        message,
        messagesUrl
    ) => {
        if (
            !message
            || message.is_muted
            || window.location.pathname
                .startsWith('/messages')
        ) {
            return;
        }

        document
            .querySelector(
                '[data-live-message-toast]'
            )
            ?.remove();

        const toast =
            document.createElement('div');

        toast.dataset.liveMessageToast = '';

        toast.className = [
            'fixed',
            'right-4',
            'top-4',
            'z-[5000]',
            'w-[calc(100%-2rem)]',
            'max-w-sm',
            'rounded-2xl',
            'border',
            'border-cyan-200',
            'bg-white',
            'p-4',
            'text-zinc-900',
            'shadow-2xl',
            'transition',
            'duration-200',
            'dark:border-cyan-400/20',
            'dark:bg-slate-900',
            'dark:text-white',
            'sm:right-6',
            'sm:top-6',
        ].join(' ');

        const senderName =
            message.sender_name
            || 'a FestFriends member';

        const destination =
            message.conversation_url
            || messagesUrl
            || '/messages';

        toast.innerHTML = `
            <div class="flex items-start gap-3">
                <div
                    class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-100 text-lg dark:bg-cyan-400/10"
                    aria-hidden="true"
                >
                    💬
                </div>

                <div class="min-w-0 flex-1">
                    <p class="font-black">
                        New message
                    </p>

                    <p
                        class="mt-1 text-sm text-zinc-600 dark:text-zinc-300"
                        data-live-message-toast-text
                    ></p>

                    <a
                        href="${destination}"
                        class="mt-3 inline-flex items-center rounded-lg bg-cyan-600 px-3 py-2 text-xs font-black text-white transition hover:bg-cyan-500"
                        data-live-message-toast-open
                    >
                        ${
                            document.querySelector(
                                '[data-live-map-message-drawer]'
                            )
                                ? 'Open Chat'
                                : 'View Messages'
                        }
                    </a>
                </div>

                <button
                    type="button"
                    class="rounded-lg p-1 text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-white/10 dark:hover:text-white"
                    data-live-message-toast-close
                    aria-label="Dismiss new message alert"
                >
                    ✕
                </button>
            </div>
        `;

        const text =
            toast.querySelector(
                '[data-live-message-toast-text]'
            );

        if (text) {
            text.textContent =
                `You have a new message from ${senderName}.`;
        }

        toast
            .querySelector(
                '[data-live-message-toast-close]'
            )
            ?.addEventListener(
                'click',
                () => removeToast(toast)
            );

        const openAction =
            toast.querySelector(
                '[data-live-message-toast-open]'
            );

        if (
            openAction
            && document.querySelector(
                '[data-live-map-message-drawer]'
            )
            && message.compact_url
        ) {
            openAction.addEventListener(
                'click',
                (event) => {
                    event.preventDefault();

                    window.dispatchEvent(
                        new CustomEvent(
                            'festfriends:open-conversation',
                            {
                                detail: {
                                    conversation_id:
                                        message
                                            .conversation_id,

                                    conversation_url:
                                        message
                                            .conversation_url,

                                    compact_url:
                                        message
                                            .compact_url,

                                    send_url:
                                        message
                                            .send_url,
                                },
                            }
                        )
                    );

                    removeToast(toast);
                }
            );
        }

        document.body.appendChild(toast);

        window.setTimeout(
            () => removeToast(toast),
            10000
        );
    };

    const refreshUnreadState =
        async ({
            allowToast = true,
        } = {}) => {
            if (
                requestInFlight
                || document.visibilityState
                    === 'hidden'
            ) {
                return;
            }

            requestInFlight = true;

            try {
                const response = await fetch(
                    stateUrl,
                    {
                        method: 'GET',
                        credentials: 'same-origin',
                        headers: {
                            Accept: 'application/json',
                            'X-Requested-With':
                                'XMLHttpRequest',
                        },
                        cache: 'no-store',
                    }
                );

                if (!response.ok) {
                    return;
                }

                const payload =
                    await response.json();

                updateBadges(
                    payload
                        .unread_conversation_count
                );

                const latestMessage =
                    payload
                        .latest_unread_message
                    ?? null;

                const nextLatestId =
                    latestMessage?.id
                        ? Number.parseInt(
                            String(
                                latestMessage.id
                            ),
                            10
                        )
                        : null;

                if (
                    allowToast
                    && nextLatestId
                    && nextLatestId
                        > latestUnreadMessageId
                ) {
                    showNewMessageToast(
                        latestMessage,
                        payload.messages_url
                    );
                }

                latestUnreadMessageId =
                    Math.max(
                        latestUnreadMessageId,
                        nextLatestId ?? 0
                    );
            } catch (error) {
                console.debug(
                    'Unable to refresh unread message state.',
                    error
                );
            } finally {
                requestInFlight = false;
            }
        };

    window.refreshUnreadMessageState =
        refreshUnreadState;

    window.addEventListener(
        'festfriends:message-read-state-changed',
        () => {
            refreshUnreadState({
                allowToast: false,
            });
        }
    );

    const schedulePolling = () => {
        window.clearInterval(pollTimer);

        pollTimer = window.setInterval(
            () => refreshUnreadState(),
            15000
        );
    };

    refreshUnreadState({
        allowToast: false,
    });

    schedulePolling();

    document.addEventListener(
        'visibilitychange',
        () => {
            if (
                document.visibilityState
                === 'visible'
            ) {
                refreshUnreadState();
            }
        }
    );
};

document.addEventListener(
    'DOMContentLoaded',
    initializeMessageUnreadMonitor
);

document.addEventListener(
    'livewire:navigated',
    initializeMessageUnreadMonitor
);

initializeMessageUnreadMonitor();
